/**
 * scheduleExchangeRates.ts
 * Frontend service for the ExchangeRatePeriod API (Phase 1, Item 8).
 *
 * All requests go through apiClient (MSAL auth + error capture).
 * Endpoints:
 *   GET    /api/exchange-rates                          — list (optional ?currency=)
 *   GET    /api/exchange-rates/rate-at?date=&currency=  — lookup rate at a date
 *   GET    /api/exchange-rates/:key                     — single
 *   POST   /api/exchange-rates                          — create
 *   PUT    /api/exchange-rates/:key                     — update
 *   DELETE /api/exchange-rates/:key                     — soft delete
 *
 *   GET    /api/exchange-rates/current                  — current rate per currency
 *   PUT    /api/exchange-rates/current                  — set/replace today's rate
 *   DELETE /api/exchange-rates/current/:currency        — remove a currency's rate
 */

import apiClient from './apiClient';
import type { ExchangeRatePeriod } from '../types/schedule';

export interface CreateExchangeRatePeriodPayload {
  Currency: string;          // ISO 4217, e.g. 'USD'
  RateToCAD: number;         // positive decimal, e.g. 1.36
  EffectiveFrom: string;     // 'YYYY-MM-DD'
  EffectiveTo?: string | null; // 'YYYY-MM-DD' or null = open-ended
  /**
   * When true and an open-ended period already exists for the same currency,
   * the API will automatically close it one day before EffectiveFrom.
   */
  closeOpenPeriod?: boolean;
}

export interface UpdateExchangeRatePeriodPayload {
  Currency?: string;
  RateToCAD?: number;
  EffectiveFrom?: string;
  EffectiveTo?: string | null;
}

export interface RateAtResponse {
  currency: string;
  date: string;
  rateToCAD: number;
}

export async function fetchExchangeRatePeriods(opts: {
  currency?: string;
} = {}): Promise<ExchangeRatePeriod[]> {
  const params: Record<string, string> = {};
  if (opts.currency !== undefined) params.currency = opts.currency;
  const response = await apiClient.get<ExchangeRatePeriod[]>('/exchange-rates', { params });
  return response.data;
}

export async function fetchExchangeRatePeriod(key: string): Promise<ExchangeRatePeriod> {
  const response = await apiClient.get<ExchangeRatePeriod>(`/exchange-rates/${key}`);
  return response.data;
}

/**
 * Look up the exchange rate for `currency` on `date`.
 * Returns null if no period covers that date (API returns 404).
 */
export async function fetchRateAt(
  date: string,
  currency: string,
): Promise<RateAtResponse | null> {
  try {
    const response = await apiClient.get<RateAtResponse>('/exchange-rates/rate-at', {
      params: { date, currency },
    });
    return response.data;
  } catch (err: unknown) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const status = (err as any)?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}

export async function createExchangeRatePeriod(
  data: CreateExchangeRatePeriodPayload,
): Promise<ExchangeRatePeriod> {
  const response = await apiClient.post<ExchangeRatePeriod>('/exchange-rates', data);
  return response.data;
}

export async function updateExchangeRatePeriod(
  key: string,
  data: UpdateExchangeRatePeriodPayload,
): Promise<ExchangeRatePeriod> {
  const response = await apiClient.put<ExchangeRatePeriod>(`/exchange-rates/${key}`, data);
  return response.data;
}

export async function deleteExchangeRatePeriod(key: string): Promise<void> {
  await apiClient.delete(`/exchange-rates/${key}`);
}

// ---------------------------------------------------------------------------
// Current-rate endpoints (simple per-currency table — Admin → Exchange Rates).
//
// These wrap the history-preserving "current rate" slice of the API. They
// hide the period/date-range mechanics: callers see one current RateToCAD per
// currency. Direction matches the rest of the module — RateToCAD is CAD per 1
// unit of the currency (so 1 USD = 1.36 CAD).
//
//   GET    /api/exchange-rates/current            — current rate per currency
//   PUT    /api/exchange-rates/current            — set/replace today's rate
//   DELETE /api/exchange-rates/current/:currency  — remove a currency's rate
// ---------------------------------------------------------------------------

/** One row in the current-rates table (CAD is never included — it is the base). */
export interface CurrentExchangeRate {
  /** ISO 4217 code, 3 uppercase letters, never 'CAD'. */
  currency: string;
  /** CAD per 1 unit of currency, or null when no active rate is set today. */
  rateToCAD: number | null;
  /** 'YYYY-MM-DD' the active period starts, or null when unset. */
  effectiveFrom: string | null;
  /** ISO-8601 timestamp of the active period's last modification, or null. */
  updatedAt: string | null;
}

export interface SetCurrentRatePayload {
  /** REQUIRED — exactly 3 letters; uppercased server-side; must not be 'CAD'. */
  currency: string;
  /** REQUIRED — finite, > 0, <= 100000 (CAD per 1 unit of currency). */
  rateToCAD: number;
}

/**
 * Fetch the current rate for every default-set currency (AUD, EUR, GBP, USD)
 * plus any other currency with an active period covering today. Sorted
 * ascending by currency; CAD is never returned.
 */
export async function fetchCurrentRates(): Promise<CurrentExchangeRate[]> {
  const response = await apiClient.get<CurrentExchangeRate[]>('/exchange-rates/current');
  return response.data;
}

/**
 * Set (or replace) today's rate for a currency. History-preserving: the first
 * set of the day closes the prior open period and opens a new one; a second
 * set the same day updates today's row in place. Returns the refreshed
 * current-rates list (same shape as fetchCurrentRates) so callers can replace
 * their state directly.
 */
export async function setCurrentRate(
  payload: SetCurrentRatePayload,
): Promise<CurrentExchangeRate[]> {
  const response = await apiClient.put<CurrentExchangeRate[]>('/exchange-rates/current', payload);
  return response.data;
}

/**
 * Remove a currency's current rate (idempotent soft-delete of its active
 * period). Returns the refreshed current-rates list. After removal the
 * currency appears only if it is in the default set [AUD, EUR, GBP, USD]
 * (with rateToCAD = null); otherwise it is absent.
 */
export async function removeCurrencyRate(currency: string): Promise<CurrentExchangeRate[]> {
  const code = currency.trim().toUpperCase();
  const response = await apiClient.delete<CurrentExchangeRate[]>(
    `/exchange-rates/current/${encodeURIComponent(code)}`,
  );
  return response.data;
}
